import type { BlogPostMeta } from '../content/blog/posts.ts';
import {
  getComparisonBySlug,
  type ComparisonEntry,
} from '../content/comparisons/index.ts';

export const SITE_URL = 'https://cently.ai';

export const FB_APP_ID = '1456362135844708';

/** 1200×630 — standard Open Graph / Twitter large card */
export const OG_IMAGE_DESKTOP = `${SITE_URL}/screenshots/desktop.png`;
/** 1200×1200 — square fallback for platforms that prefer 1:1 previews */
export const OG_IMAGE_SQUARE = `${SITE_URL}/screenshots/og-square.png`;

const OG_IMAGE_ALT =
  'Cently app showing spending insights and AI chat on web and mobile';

export type SeoEntry = {
  title: string;
  description: string;
  image?: string;
  ogType?: string;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string[];
  /**
   * Compliance-only utility pages (e.g. the account-deletion URL required by
   * app store policy) that should keep an accurate title/canonical but stay
   * out of the sitemap and search results. Not linked from site nav.
   */
  noIndex?: boolean;
};

const defaultEntry: SeoEntry = {
  title: 'Cently: spending tracker and budgeting app with AI answers',
  description:
    'Connect bank and card accounts, ask questions in plain English, and get clear answers and next steps on web, iOS, and Android with Cently.',
  image: OG_IMAGE_DESKTOP,
  ogType: 'website',
  keywords: [
    'personal finance',
    'spending tracker',
    'money habits',
    'budgeting',
    'transaction analysis',
    'financial clarity',
  ],
};

export const seoEntries: Record<string, SeoEntry> = {
  '/': defaultEntry,
  '/features': {
    title: 'Automatic spending tracker and budgeting features',
    description:
      'Sync accounts, categorize transactions, parse receipts, and use AI insights to stay on top of your budget on web, iOS, and Android.',
    keywords: [
      'bank sync',
      'automated tracking',
      'email receipt forwarding',
      'spending insights',
      'transaction categorization',
      'personal finance features',
    ],
  },
  '/about': {
    title: 'Why we built a simpler way to track your spending',
    description:
      'Learn why Cently exists and how we help people understand spending, improve money habits, and make better financial decisions.',
    keywords: [
      'Cently team',
      'financial clarity',
      'money management',
      'personal finance app',
    ],
  },
  '/contact': {
    title: 'Contact support: account, billing, and partnerships',
    description:
      'Get help with your Cently account, features, billing, or partnerships. Our team is ready to help.',
    keywords: ['Cently support', 'help center', 'contact us', 'partnerships'],
  },
  '/privacy': {
    title: 'Privacy policy: how we protect your financial data',
    description:
      'How we handle your data. We take privacy seriously and share our security practices and data handling.',
    keywords: ['data privacy', 'security', 'financial data security'],
  },
  '/delete-account': {
    title: 'Delete your Cently account',
    description:
      'Steps to delete your Cently account, plus what data is removed right away and what we keep for legal and anti-fraud reasons.',
    noIndex: true,
  },
  '/security': {
    title: 'Is Cently safe? Security and data protection explained',
    description:
      'See how Cently protects your financial data with SOC 2 Type II audited infrastructure, encryption, and true data deletion.',
    keywords: [
      'is cently safe',
      'cently security',
      'financial data security',
      'is cently legit',
      'is cently real',
    ],
  },
  '/terms': {
    title: 'Terms of service',
    description:
      'The terms and conditions for using Cently. Fair, clear, and easy to understand.',
    keywords: ['terms of service', 'legal', 'user agreement'],
  },
  '/changelog': {
    title: 'Changelog: product updates and release notes',
    description:
      "See what we've shipped and what's coming next. Track updates to Cently on web, iOS, and Android.",
    keywords: [
      'changelog',
      'product updates',
      'release notes',
      'personal finance updates',
    ],
  },
  '/blog': {
    title: 'Blog: spending tips, budgeting ideas, and updates',
    description:
      'Read practical spending tips, budgeting ideas, and Cently product updates to build clearer money habits.',
    keywords: [
      'finance blog',
      'spending tips',
      'money habits',
      'Cently updates',
    ],
  },
  '/compare': {
    title: 'Compare Cently with other budgeting and finance apps',
    description:
      'Compare Cently with popular finance apps and see differences in AI insights, budgeting workflows, receipt tracking, and platform support.',
    keywords: [
      'finance app comparisons',
      'budget app alternatives',
      'mint alternative',
      'rocket money alternative',
      'ynab alternative',
    ],
  },
};

function ogImageMeta(image: string, includeSquareFallback: boolean) {
  const tags: Array<Record<string, string>> = [
    { property: 'og:image', content: image },
    { property: 'og:image:alt', content: OG_IMAGE_ALT },
  ];

  if (image === OG_IMAGE_DESKTOP) {
    tags.push(
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:type', content: 'image/png' },
    );
  }

  if (includeSquareFallback) {
    tags.push(
      { property: 'og:image', content: OG_IMAGE_SQUARE },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '1200' },
      { property: 'og:image:type', content: 'image/png' },
    );
  }

  return tags;
}

function withCanonical(
  pathname: string,
  entry: SeoEntry,
): SeoEntry & { canonical: string } {
  // TanStack Router + hosting may produce either "/about" or "/about/".
  // Keep lookup keys stable and canonicalize to NO trailing slash.
  const lookup = pathname === '/' ? '/' : pathname.replace(/\/$/, '') || '/';
  return {
    ...entry,
    canonical: `${SITE_URL}${lookup}`,
    ogType: entry.ogType ?? defaultEntry.ogType,
    image: OG_IMAGE_DESKTOP,
  };
}

export function buildHead(pathname: string) {
  const entry = getSeoEntry(pathname);
  const ogType = entry.ogType ?? 'website';

  const meta = [
    { title: entry.title },
    { name: 'description', content: entry.description },
    { property: 'og:title', content: entry.title },
    { property: 'og:description', content: entry.description },
    { property: 'og:url', content: entry.canonical },
    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: 'Cently' },
    ...ogImageMeta(OG_IMAGE_DESKTOP, true),
    { property: 'fb:app_id', content: FB_APP_ID },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: entry.title },
    { name: 'twitter:description', content: entry.description },
    { name: 'twitter:image', content: OG_IMAGE_DESKTOP },
    { name: 'twitter:image:alt', content: OG_IMAGE_ALT },
  ];

  if (entry.keywords?.length) {
    meta.push({ name: 'keywords', content: entry.keywords.join(', ') });
  }

  if (entry.noIndex) {
    meta.push({ name: 'robots', content: 'noindex, nofollow' });
  }

  return {
    meta,
    links: [{ rel: 'canonical', href: entry.canonical }],
  };
}

export function getSeoEntry(pathname: string) {
  const lookup = pathname === '/' ? '/' : pathname.replace(/\/$/, '') || '/';
  return withCanonical(lookup, seoEntries[lookup] ?? defaultEntry);
}

export function buildNotFoundHead() {
  const title = 'Page not found';
  const description =
    'This page doesn’t exist on cently.ai. Jump back to the highlights or get the app to keep going.';

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { name: 'robots', content: 'noindex, follow' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Cently' },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
  };
}

export function buildBlogHead(post: BlogPostMeta) {
  const entry = blogEntryToSeo(post);

  const meta = [
    { title: entry.title },
    { name: 'description', content: entry.description },
    { property: 'og:title', content: entry.title },
    { property: 'og:description', content: entry.description },
    { property: 'og:url', content: entry.canonical },
    { property: 'og:type', content: entry.ogType ?? 'article' },
    { property: 'og:site_name', content: 'Cently' },
    ...ogImageMeta(OG_IMAGE_DESKTOP, true),
    { property: 'fb:app_id', content: FB_APP_ID },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: entry.title },
    { name: 'twitter:description', content: entry.description },
    { name: 'twitter:image', content: OG_IMAGE_DESKTOP },
    { name: 'twitter:image:alt', content: OG_IMAGE_ALT },
  ];

  if (entry.publishedTime) {
    meta.push({
      property: 'article:published_time',
      content: entry.publishedTime,
    });
  }
  if (entry.modifiedTime) {
    meta.push({
      property: 'article:modified_time',
      content: entry.modifiedTime,
    });
  }
  if (entry.keywords?.length) {
    meta.push({ name: 'keywords', content: entry.keywords.join(', ') });
  }

  return {
    meta,
    links: [{ rel: 'canonical', href: entry.canonical }],
  };
}

export function buildCompareHead(slug: string) {
  const entry = getComparisonBySlug(slug);
  if (!entry) {
    return buildHead('/compare');
  }
  const seoEntry = comparisonEntryToSeo(entry);

  const meta = [
    { title: seoEntry.title },
    { name: 'description', content: seoEntry.description },
    { property: 'og:title', content: seoEntry.title },
    { property: 'og:description', content: seoEntry.description },
    { property: 'og:url', content: seoEntry.canonical },
    { property: 'og:type', content: seoEntry.ogType ?? 'website' },
    { property: 'og:site_name', content: 'Cently' },
    ...ogImageMeta(OG_IMAGE_DESKTOP, true),
    { property: 'fb:app_id', content: FB_APP_ID },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: seoEntry.title },
    { name: 'twitter:description', content: seoEntry.description },
    { name: 'twitter:image', content: OG_IMAGE_DESKTOP },
    { name: 'twitter:image:alt', content: OG_IMAGE_ALT },
  ];

  if (seoEntry.keywords?.length) {
    meta.push({ name: 'keywords', content: seoEntry.keywords.join(', ') });
  }

  return {
    meta,
    links: [{ rel: 'canonical', href: seoEntry.canonical }],
  };
}

export function blogEntryToSeo(
  post: BlogPostMeta,
): SeoEntry & { canonical: string; ogType: 'article' } {
  const description = post.metaDescription ?? post.description;
  return {
    title: post.title,
    description,
    image: OG_IMAGE_DESKTOP,
    canonical: `${SITE_URL}/blog/${post.slug}`,
    ogType: 'article',
    publishedTime: toIsoDate(post.date),
    modifiedTime: toIsoDate(post.dateModified ?? post.date),
    keywords: post.keywords,
  };
}

export function comparisonEntryToSeo(
  entry: ComparisonEntry,
): SeoEntry & { canonical: string; ogType: 'website' } {
  return {
    title: entry.seoTitle,
    description: entry.metaDescription,
    image: OG_IMAGE_DESKTOP,
    canonical: `${SITE_URL}/compare/${entry.slug}`,
    ogType: 'website',
    keywords: entry.keywords,
  };
}

function toIsoDate(dateStr?: string) {
  if (!dateStr) return undefined;
  const date = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}
